import {
  datadogLogs,
  LogsInitConfiguration,
  LogsEvent,
} from '@datadog/browser-logs'
import {
  datadogRum,
  RumErrorEvent,
  RumEvent,
  RumInitConfiguration,
} from '@datadog/browser-rum'
import { EnvironmentName, resolveEnvironment } from '@/utils/env'

function datadogEnvName(): string | null {
  if (process.env.VUE_APP_DD_ENV_OVERRIDE) {
    // This is useful when testing Datadog integration or setup during local development,
    // but shouldn't be used outside of that.
    return process.env.VUE_APP_DD_ENV_OVERRIDE
  }
  const environmentName: EnvironmentName = resolveEnvironment()
  if (environmentName === EnvironmentName.dev) {
    return 'dev'
  } else if (environmentName === EnvironmentName.staging) {
    return 'staging'
  } else if (environmentName === EnvironmentName.production) {
    return 'production'
  } else {
    return null
  }
}
/**
 * Update the Datadog current session with information about the active user.
 */
export function setUserContext(
  id: string | null,
  email: string | null,
  name: string | null
): void {
  datadogRum.setUser({
    id,
    email,
    name,
  })
}
/**
 * Update the Datadog current session with information about the active user.
 */
export function clearUserContext(): void {
  datadogRum.stopSession()
  datadogRum.clearUser()
}

export const EMPTY_USER_ROLES_EVENT = 'auth.user_profile.empty_roles'

/**
 * Log when getUserProfile returns an empty roles array.
 * Emits a structured error log that can be monitored in Datadog.
 */
export function logEmptyUserRoles(userId: number | null): void {
  if (datadogEnvName() !== null) {
    datadogLogs.logger.error('C4O User has no roles assigned', {
      'evt.name': EMPTY_USER_ROLES_EVENT,
      userId,
    })
    return
  }

  console.error('No roles found for user', userId)
}

export function initDatadog(): void {
  const DATADOG_ENV = datadogEnvName()
  if (DATADOG_ENV !== null) {
    // we're running in one of our deployed environments, initialize datadog
    const DATADOG_APPLICATION_ID = '4dc84775-8487-4f9d-bb0a-35612ef5adba'
    const DATADOG_CLIENT_TOKEN = 'pub266fa6a6625211a30e070b9b3d9f97d2'
    const DATADOG_SITE = 'us5.datadoghq.com'
    const DATADOG_SERVICE = 'charterup-operators-ui'
    const DATADOG_APP_VERSION = process.env.VUE_APP_BUILD_VERSION || 'unknown'

    const DATADOG_CONFIG: RumInitConfiguration & LogsInitConfiguration = {
      applicationId: DATADOG_APPLICATION_ID,
      clientToken: DATADOG_CLIENT_TOKEN,
      site: DATADOG_SITE,
      service: DATADOG_SERVICE,
      env: DATADOG_ENV,
      version: DATADOG_APP_VERSION,
      // Browser RUM config
      sessionSampleRate: 100,
      sessionReplaySampleRate: 50,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
      // Browser APM tracing config
      traceSampleRate: 20,
      traceContextInjection: 'sampled',
      allowedTracingUrls: [
        {
          match: /https:\/\/.*\.operators\.charterup\.com/,
          propagatorTypes: ['datadog', 'tracecontext'],
        },
      ],
      // Logging configuration
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['warn', 'error'],
      forwardReports: 'all',
    }

    const shouldFilterNetworkError = (event: RumErrorEvent | LogsEvent) => {
      const isNetworkError =
        event.error?.message === 'Network Error' ||
        event.error?.message === 'AxiosError: Network Error' ||
        event.error?.stack?.includes('Network Error')

      return isNetworkError && event.error?.handling === 'unhandled'
    }

    const shouldFilter401Error = (event: RumErrorEvent | LogsEvent) => {
      const is401Error =
        event.error?.message?.includes('status code 401') ||
        event.error?.stack?.includes('status code 401')

      return is401Error
    }

    datadogLogs.init({
      ...DATADOG_CONFIG,
      beforeSend: (event: LogsEvent) => {
        if (shouldFilterNetworkError(event) || shouldFilter401Error(event)) {
          return false
        }
        return true
      },
    })

    datadogRum.init({
      ...DATADOG_CONFIG,
      beforeSend: (event: RumEvent) => {
        if (
          event.type === 'error' &&
          shouldFilterNetworkError(event as RumErrorEvent)
        ) {
          return false
        }
        return true
      },
    })
  } else {
    console.log('Skipping datadog instrumentation')
  }
}
